// What a grid cell shows: the shared states of the week grid, the board and
// the bench. Pure, so the three screens agree and it can be tested.
import type { Cell, Day, Half, SessionRow } from "./types";
import { slotBefore } from "./days";

export type BlockState = "planned" | "now" | "done" | "short" | "under" | "unlogged" | "float" | "off";

export interface BlockInfo {
  state: BlockState;
  /** Units made, when a session was logged. */
  made: number | null;
  /** The cell's target: its planned quantity, reduced for a learner. */
  target: number | null;
  /** Locked once its half-day has started (plus 30 min) or a session is logged. */
  locked: boolean;
  /** Share of target made, 0..n. */
  share: number | null;
}

/** Learners are planned at a share of the full session (25/50/75% by week; 50% when the API doesn't say). */
export const LEARNER_SHARE = 0.5;

export function targetFor(cell: Pick<Cell, "qty" | "learner">, sessionQty: number | null | undefined): number | null {
  if (cell.qty != null) return cell.qty;
  if (sessionQty == null) return null;
  return Math.round(sessionQty * (cell.learner ? LEARNER_SHARE : 1));
}

/**
 * done ≥ 90% of target; short 70–89%, or below 90% with an issue reported
 * (the reason is known, so it is not just "under"); under < 70%; unlogged is a
 * past half-day nobody logged (unknown, not bad).
 */
export function resultOf(made: number, target: number | null, issue: boolean): BlockState {
  if (!target) return made > 0 ? "done" : issue ? "short" : "under";
  const share = made / target;
  if (share >= 0.9) return "done";
  if (share >= 0.7 || issue) return "short";
  return "under";
}

export function blockInfo(
  cell: Cell,
  opts: {
    session?: SessionRow | null;
    sessionQty?: number | null;
    now?: { day: Day; half: Half } | null;
    lockedBefore?: { day: Day; half: Half } | null;
  } = {}
): BlockInfo {
  const target = cell.kind === "task" ? targetFor(cell, opts.sessionQty) : null;
  const logged = opts.session && opts.session.status === "logged" ? opts.session : null;
  const locked = !!logged || slotBefore(cell.day, cell.half, opts.lockedBefore ?? null);
  const base = { target, locked, made: logged?.actualQty ?? null, share: null as number | null };
  if (cell.kind === "off") return { ...base, state: "off" };
  if (logged && logged.actualQty != null) {
    const state = cell.kind === "float" && !target ? "done" : resultOf(logged.actualQty, target, !!logged.issue);
    return { ...base, state, share: target ? logged.actualQty / target : null };
  }
  const isNow = !!opts.now && opts.now.day === cell.day && opts.now.half === cell.half;
  if (isNow) return { ...base, state: cell.kind === "float" ? "float" : "now" };
  const past = slotBefore(cell.day, cell.half, opts.now ?? null);
  if (cell.kind === "float") return { ...base, state: "float" };
  if (past) return { ...base, state: "unlogged" };
  return { ...base, state: "planned" };
}

/** The session logged against a cell (same person, day, half). */
export function sessionFor(sessions: SessionRow[], cell: Pick<Cell, "person" | "day" | "half">): SessionRow | null {
  return sessions.find((s) => s.person === cell.person && s.day === cell.day && s.half === cell.half && s.status === "logged") ?? null;
}
